// ENG2-2455 (T4, Part A): which of a customer's values differ from its plan
// template. Feeds the Restore Defaults dialog.
//
// The customer side is CustomerRestrictionsResponse (rights = API rights
// merged with legacy_rights, caps, max_users). The template side is the full
// restriction_templates row (DefaultPlan). Rules per value type are in the
// test file next to this one; the short version:
//
//   - RisikaAPI resolves the template's `place_holder_country` to the
//     customer's own country (refresh_restrictions), so we do the same before
//     comparing allow_countries and score models
//   - a global flag or boolean right the template does not carry means false
//     (allow_ai is missing from templates until RisikaAPI ENG2-2540 ships)
//   - the global data subscription dates (ENG2-2395) and the Risika for AI
//     end date (ENG2-2614) are listed when set, with no template default, so
//     CS can see and clear them here; the bulk "Apply package defaults"
//     (Part B) leaves them alone
//   - allow_zones and global_lookups_limit are zone contracts: not listed
//
// The exception objects keep the shape the dialog already renders
// (ExceptionType), so exception.tsx is untouched.

import {
  BeneficialOwnersLegitimateReason,
  CustomerRestrictionsResponse,
} from 'services/cm-api/get-customer'
import {
  DefaultPlan,
  TemplateScoreModel,
  templateCountryModels,
  templateGlobalFlags,
} from 'services/cm-api/get-default-plans'
import { ExceptionType, NewValue } from './restrictions.types'
// One naming rule for the Rights section and Restore Defaults (ENG2-2454):
// rightLabel gives the model label (Risk Insights, Credit Automator, Credit
// Requests, PDF Reports…); caps keep the readable key (camelize).
import { camelize, isHiddenRight, rightLabel } from './rights-sections'

export const BOOLEAN_RIGHTS = [
  'allow_api',
  'allow_ai',
  'allow_basic',
  'allow_browser_extension',
  'allow_company_export',
  'allow_company_search',
  'allow_credit',
  'allow_ean',
  'allow_financial',
  'allow_hierarchy',
  'allow_highlights',
  'allow_monitoring',
  'allow_monitoring_export',
  'allow_notes',
  'allow_pdf_reports',
  'allow_person_export',
  'allow_person_search',
  'allow_relations',
  'allow_sales_triggers',
] as const

export const NUMERIC_RIGHTS = ['tier', 'search_exports_limit'] as const

// Boolean flags under rights.global.
export const GLOBAL_FLAGS = [
  'observational_lists',
  'enterprise_credit_policy',
  'fraud_indicators',
  'credit_override',
  'global_data',
  'beneficial_owners',
] as const

// Contract dates under rights.global (ENG2-2395, ENG2-2614): a set date is a
// customisation with no template counterpart.
export const GLOBAL_DATES = [
  'global_data_subscription_start_date',
  'global_data_subscription_end_date',
  'ai_end_date',
] as const

export const CAPS = [
  'credit_ratings',
  'credit_reports',
  'lookups',
  'monitoring_count',
  'monitoring_exports',
  'search_exports',
] as const

export const COUNTRIES = ['DK', 'FI', 'NO', 'SE'] as const

const COUNTRY_LABELS: Record<string, string> = {
  DK: 'Denmark',
  FI: 'Finland',
  NO: 'Norway',
  SE: 'Sweden',
}

const PLACEHOLDER = 'PLACE_HOLDER_COUNTRY'

type BeneficialOwners = {
  enabled: boolean
  default_legitimate_reason: BeneficialOwnersLegitimateReason | null
}

const DISABLED_BENEFICIAL_OWNERS: BeneficialOwners = {
  enabled: false,
  default_legitimate_reason: null,
}

// The API returns `false` for a customer who never had the add-on and an
// object once it was configured; both mean the same thing when disabled.
const normaliseBeneficialOwners = (value: unknown): BeneficialOwners => {
  if (value && typeof value === 'object' && 'enabled' in value) {
    const v = value as BeneficialOwners
    return v.enabled
      ? {
          enabled: true,
          default_legitimate_reason: v.default_legitimate_reason ?? null,
        }
      : DISABLED_BENEFICIAL_OWNERS
  }
  return DISABLED_BENEFICIAL_OWNERS
}

const sameSet = (a: string[], b: string[]): boolean => {
  if (a.length !== b.length) return false
  const sorted = (xs: string[]) => [...xs].sort()
  const sa = sorted(a)
  const sb = sorted(b)
  return sa.every((x, i) => x === sb[i])
}

const modelKey = (m: TemplateScoreModel) =>
  `${m.model}:${m.explanation ? 1 : 0}`

const templateCountries = (
  template: DefaultPlan,
  customerCountry: string,
): string[] =>
  template.allow_countries.map((c) =>
    c.toUpperCase() === PLACEHOLDER
      ? customerCountry.toUpperCase()
      : c.toUpperCase(),
  )

const templateScoreModels = (
  template: DefaultPlan,
  country: string,
  customerCountry: string,
): TemplateScoreModel[] => {
  const own = templateCountryModels(template.rights, country)
  if (own) return own
  if (country === customerCountry.toUpperCase()) {
    return templateCountryModels(template.rights, 'place_holder_country') ?? []
  }
  return []
}

export const findPlanExceptions = (
  customer: CustomerRestrictionsResponse | undefined,
  template: DefaultPlan | undefined,
  customerCountry: string | undefined,
): ExceptionType[] => {
  if (!customer || !template) return []
  const rights = (customer.rights ?? {}) as unknown as Record<string, unknown>
  const country = (customerCountry ?? '').toUpperCase()
  const rows: ExceptionType[] = []

  const push = (
    key: string,
    type: ExceptionType['type'],
    label: string,
    currValue: NewValue,
    defaultValue: NewValue,
    isGlobal?: boolean,
  ) => {
    // A right the Rights section hides is not offered for restore either
    // (Browser Extension, ENG2-2457). Apply defaults still resets it.
    if (isHiddenRight(key)) return
    rows.push({ key, type, label, currValue, defaultValue, isGlobal })
  }

  if (customer.max_users !== template.max_users) {
    push(
      'max_users',
      'right',
      'Max users',
      customer.max_users,
      template.max_users,
    )
  }

  BOOLEAN_RIGHTS.forEach((right) => {
    const curr = rights[right]
    if (typeof curr !== 'boolean') return
    const def = template[right] ?? false
    if (curr !== def) push(right, 'right', rightLabel(right), curr, def)
  })

  NUMERIC_RIGHTS.forEach((right) => {
    const curr = rights[right]
    if (typeof curr !== 'number') return
    const def = template[right]
    if (curr !== def) push(right, 'right', rightLabel(right), curr, def)
  })

  if (Array.isArray(rights.allow_countries)) {
    const curr = (rights.allow_countries as string[]).map((c) =>
      c.toUpperCase(),
    )
    const def = templateCountries(template, country)
    if (!sameSet(curr, def)) {
      push('allow_countries', 'right', rightLabel('allow_countries'), curr, def)
    }
  }

  if (typeof rights.allow_credit_policy === 'string') {
    const curr = rights.allow_credit_policy as string
    const def = template.allow_credit_policy
    if (curr !== def) {
      push(
        'allow_credit_policy',
        'right',
        rightLabel('allow_credit_policy'),
        curr,
        def,
      )
    }
  }

  const global = (rights.global ?? {}) as Record<string, unknown>
  const templateGlobal = templateGlobalFlags(template.rights)
  GLOBAL_FLAGS.forEach((flag) => {
    if (!(flag in global)) return
    if (flag === 'beneficial_owners') {
      const curr = normaliseBeneficialOwners(global[flag])
      const def = normaliseBeneficialOwners(templateGlobal[flag])
      if (
        curr.enabled !== def.enabled ||
        curr.default_legitimate_reason !== def.default_legitimate_reason
      ) {
        push(flag, 'right', rightLabel(flag), curr, def, true)
      }
      return
    }
    const curr = Boolean(global[flag])
    const def = Boolean(templateGlobal[flag])
    if (curr !== def) push(flag, 'right', rightLabel(flag), curr, def, true)
  })

  GLOBAL_DATES.forEach((key) => {
    const curr = global[key]
    if (typeof curr === 'string' && curr !== '') {
      push(key, 'right', rightLabel(key), curr, null, true)
    }
  })

  CAPS.forEach((cap) => {
    const curr = customer.caps?.[cap]
    const def = template[cap]
    if (curr !== def) push(cap, 'cap', camelize(cap), curr, def)
  })

  COUNTRIES.forEach((code) => {
    const entry = rights[code] as
      | { score_models?: TemplateScoreModel[] }
      | undefined
    if (!entry || !Array.isArray(entry.score_models)) return
    const curr = entry.score_models
    const def = templateScoreModels(template, code, country)
    if (!sameSet(curr.map(modelKey), def.map(modelKey))) {
      push(code, 'country', COUNTRY_LABELS[code] ?? code, curr, def)
    }
  })

  return rows
}
